import { useMemo, useState } from 'react';
import { Err, Ok } from 'ts-results';
import { useSendBatch } from '~/hooks/useBatches';
import { useExperiment } from '~/systems/experiment';
import type { Result } from 'ts-results';
import type {
  CloudEmulationParams,
  DeviceType,
  JSend,
  Project
} from '~/api/types';

export type CloudSubmissionResult = Result<string, string>;

const supportedDevices: DeviceType[] = ['EMU_FREE', 'EMU_MPS', 'EMU_SV'];

/**
 * Hook exposing Pasqal Cloud Services features.
 * Replaces the Vue usePCS composable.
 */
export const usePCS = () => {
  const {
    commitedAtoms: atoms,
    exportPulser,
    commitedPulses: pulses
  } = useExperiment();
  const [currentProject, setCurrentProject] = useState<Project | undefined>(
    undefined
  );
  const [submissionResults, setSubmissionResults] = useState<
    CloudSubmissionResult[]
  >([]);
  const [cloudEmulationParams, setCloudEmulationParams] =
    useState<CloudEmulationParams>({
      numberOfRuns: 100,
      emulator: 'EMU_MPS'
    });

  const { mutateAsync: sendBatch, isPending: isSending } = useSendBatch();

  const isSubmitDisabled = useMemo(() => {
    return (
      isSending || !currentProject || pulses.length === 0 || atoms.length === 0
    );
  }, [atoms, currentProject, isSending, pulses]);

  async function submitExperimentAsBatch(): Promise<CloudSubmissionResult> {
    if (!currentProject) {
      return Err('You need to select a project.');
    }
    try {
      const result = await sendBatch({
        sequence: JSON.stringify(exportPulser()),
        project_id: currentProject.id,
        emulator: cloudEmulationParams.emulator,
        job_runs: cloudEmulationParams.numberOfRuns
      });
      if (!result.data) {
        return Err('No batch data');
      }
      return Ok(result.data?.id);
    } catch (e: unknown) {
      return Err((e as JSend<unknown>).message);
    }
  }

  async function runOnQuantumDevice() {
    const result = await submitExperimentAsBatch();
    setSubmissionResults((prev) => [...prev, result]);
  }

  return {
    cloudEmulationParams,
    setCloudEmulationParams,
    currentProject,
    setCurrentProject,
    isSubmitDisabled,
    runOnQuantumDevice,
    submissionResults,
    supportedDevices
  };
};
